import { useEffect, useState } from 'react';
import { message } from 'antd';
import API, { CreatePaymentDto, EditClientDto } from '../../API';
import { useAddresses, useClient, useContainers, useUser } from 'reducers';
import { ClientOrder, OrderType } from 'types';

export const useClientDetails = (id: number) => {
	const [client, fetchClient] = useClient(id);
	const [orders, setOrders] = useState<ClientOrder[]>();

	useEffect(() => {
		fetchOrders();
	}, [id]);

	const fetchOrders = async () => {
		try {
			const orders = await API.orders2.client.get(id);

			setOrders(orders);
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się pobrać danych (${e})`);
		}
	};

	const [createdBy] = useUser(client?.createdBy);
	const [removedBy] = useUser(client?.removedBy ?? undefined);
	const [lockedBy] = useUser(client?.lockedBy ?? undefined);
	const addresses = useAddresses(id);
	const containers = useContainers({});

	const [loading, setLoading] = useState(false);

	const saveChanges = async (dto: EditClientDto) => {
		if (!client) {
			return;
		}

		try {
			await API.clients.patch(id, dto);
			await fetchClient();

			message.success('Zapisano zmiany');

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się zapisać zmian (${e})`);

			return false;
		}
	};

	const deleteClient = async (close: () => void) => {
		try {
			setLoading(true);

			await API.client(id).delete();
			await fetchClient();

			close();
			setLoading(false);

			message.success('Usunięto klienta');
		} catch (e) {
			setLoading(false);
			close();
			message.error(`${e}`);
			console.trace(e);
		}
	};

	const restoreClient = async () => {
		try {
			setLoading(true);

			await API.client(id).patch({ removed: null });
			await fetchClient();

			setLoading(false);
			message.success('Przywrócono klienta');
		} catch (e) {
			message.error(`${e}`);
			setLoading(false);
			console.trace(e);
		}
	};

	const lockClient = async () => {
		try {
			await API.client(id).patch({ locked: true });
			await fetchClient();

			message.success('Zablokowano klienta');
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się zablokować klienta (${e})`);
		}
	};

	const unlockClient = async () => {
		try {
			await API.client(id).patch({ locked: false });
			await fetchClient();

			message.success('Odblokowano klienta');
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się odblokować klienta (${e})`);
		}
	};

	const payOrder = async (order: ClientOrder) => {
		try {
			if (order.type === OrderType.loose) {
				await API.payments.put({
					orderId: order.id,
					orderType: OrderType.loose,
					amount: order.grossPrice - order.alreadyPaid,
					type: order.wantedPaymentType,
				} as CreatePaymentDto);
				await fetchOrders();

				message.success('Zamówienie zostało oznaczone jako opłacone');
				return;
			}

			if (order.type === OrderType.classic) {
				// TODO: Move to new endpoint
				await API.orders.patch(order.id, { payment: 1 });
				await fetchOrders();

				message.success('Oznaczono jako zapłacone');
				return;
			}

			message.error('Nie można opłacić tego typu zamówienia');
		} catch (e) {
			console.trace(e);
			message.error(`Nie udało się opłacić zamówienia (${e})`);
		}
	};

	return {
		client,
		orders,
		containers,
		createdBy,
		removedBy,
		lockedBy,
		addresses,
		loading,
		saveChanges,
		deleteClient,
		restoreClient,
		lockClient,
		unlockClient,
		payOrder,
	};
};
